import React, { useRef, useEffect, useState } from "react";
import clsx from "clsx";
import { IoClose } from "react-icons/io5";
import { useUser } from "@/context/UserContext";
import DeleteTag from "@/pages/LiveVariant/components/Social/DeleteTag";

const SCROLLBAR_HIDE_DELAY_MS = 1000; // Delay in milliseconds before hiding the scrollbar after scrolling stops

type Votes = {
    agree: number;
    disagree: number;
};

type Linker = {
    id: string;
    nickname: string;
};

type Tag = {
    id: string;
    name: string;
    votes: Votes;
    linker?: Linker;
};

type TransformedTag = Tag & {
    label: string;
    netAgrees: number;
};

type SelectedTag = {
    id: string;
    name: string;
};

type TagListProps = {
    tags?: Tag[];
    onTagClick?: (tag: TransformedTag) => void;
    selectedTag?: SelectedTag;
    propertyId?: string;
};

/**
 * TagList - horizontal scrollable row of tag chips with dynamic sticky '+N' chip at end
 * @param {Array} tags - Array of tag objects with shape:
 *   - id: string
 *   - name: string
 *   - votes: { agree: number, disagree: number }
 *   - linker: { id: string, nickname: string } - user who linked the tag to the property
 * @param {Function} onTagClick - Callback function when a tag is clicked
 * @param {Object} selectedTag - Currently selected tag object with shape:
 *   - id: string
 *   - name: string
 * @param {string} propertyId - The ID of the property these tags belong to
 */
const TagList: React.FC<TagListProps> = ({ tags = [], onTagClick, selectedTag, propertyId }) => {
    const { userState } = useUser();
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [tagToDelete, setTagToDelete] = useState<Tag | null>(null);

    // Transform tags to include label and netAgrees
    const transformedTags: TransformedTag[] = tags.map((tag) => ({
        ...tag,
        label: `#${tag.name}`,
        netAgrees: tag.votes.agree - tag.votes.disagree,
    }));

    // Sort tags by netAgrees desc
    const sortedTags = [...transformedTags].sort((a, b) => b.netAgrees - a.netAgrees);
    const showExtra = sortedTags.length > 3;
    const visibleTags = sortedTags;

    // Scroll-aware dynamic +N chip
    const scrollRef = useRef<HTMLDivElement>(null);
    const tagRefs = useRef<(HTMLButtonElement | null)[]>([]);
    const [hiddenCount, setHiddenCount] = useState(sortedTags.length - 3);
    const [isScrolling, setIsScrolling] = useState(false);
    const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Helper to count how many tags are not fully visible to the right
    const updateHiddenCount = () => {
        const el = scrollRef.current;
        if (!el || !tagRefs.current.length) return;
        // Check if at end
        if (el.scrollLeft + el.clientWidth >= el.scrollWidth - 2) {
            setHiddenCount(0);
            return;
        }
        let count = 0;
        for (let i = tagRefs.current.length - 1; i >= 0; i--) {
            const tagEl = tagRefs.current[i];
            if (!tagEl) continue;
            // If the right edge of the tag is greater than the visible right edge of the container
            const tagRight = tagEl.offsetLeft + tagEl.offsetWidth;
            const containerRight = el.scrollLeft + el.clientWidth;
            if (tagRight > containerRight) {
                count++;
            } else {
                break; // All previous tags are visible
            }
        }
        setHiddenCount(count);
    };

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;

        const handleScroll = () => {
            setIsScrolling(true);
            updateHiddenCount();

            // Clear any existing timeout
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }

            // Set a new timeout to hide the scrollbar
            scrollTimeoutRef.current = setTimeout(() => {
                setIsScrolling(false);
            }, SCROLLBAR_HIDE_DELAY_MS); // Hide after 1 second of no scrolling
        };

        const handleResize = () => updateHiddenCount();
        el.addEventListener("scroll", handleScroll);
        window.addEventListener("resize", handleResize);
        // Initial check
        updateHiddenCount();
        return () => {
            el.removeEventListener("scroll", handleScroll);
            window.removeEventListener("resize", handleResize);
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
        };
        // eslint-disable-next-line
    }, [sortedTags.length]);

    // Width of the +N chip (should match the button's px-3, py-1, font, etc.)
    const plusNWidth = 48; // px, adjust if your +N chip is wider/narrower

    // Handler for clicking the +N chip
    const handlePlusNClick = () => {
        const el = scrollRef.current;
        if (!el) return;
        el.scrollTo({ left: el.scrollWidth - el.clientWidth, behavior: "smooth" });
    };

    // Handler for delete icon click
    const handleDeleteClick = (e: React.MouseEvent, tag: TransformedTag) => {
        e.stopPropagation(); // Prevent tag click
        setTagToDelete(tag);
        setShowDeleteConfirm(true);
    };

    const handleDeleteSuccess = (showSuccess: boolean) => {
        if (!showSuccess) {
            setTagToDelete(null);
        }
        setShowDeleteConfirm(false);
    };

    const handleDeleteCancel = () => {
        setShowDeleteConfirm(false);
        setTagToDelete(null);
    };

    // Check if current user created the property tag
    const canDeleteTag = (tag: TransformedTag) => {
        return userState?.id && tag.linker?.id === userState.id;
    };

    return (
        <>
            <div
                ref={scrollRef}
                className={clsx(
                    "tw-relative tw-flex tw-overflow-x-auto tw-gap-2 tw-py-[5px] tw-w-full tw-max-w-full tw-min-w-0",
                    isScrolling
                        ? "tw-scrollbar-thin tw-scrollbar-thumb-gray-300 tw-scrollbar-track-transparent"
                        : "tw-scrollbar-none",
                )}
                style={{
                    WebkitOverflowScrolling: "touch",
                    msOverflowStyle: "none",
                    scrollbarWidth: isScrolling ? "thin" : "none",
                }}
            >
                {visibleTags.map((tag, idx) => (
                    <button
                        key={tag.id}
                        ref={(el) => {
                            tagRefs.current[idx] = el;
                        }}
                        className={clsx(
                            "tw-bg-[#f3f0ff] tw-text-[#6c47ff] tw-font-semibold tw-px-3 tw-py-1 tw-rounded-full tw-text-xs tw-whitespace-nowrap tw-border tw-border-[#e0d7ff] hover:tw-bg-[#e9e3ff] tw-transition-colors tw-relative tw-group",
                            selectedTag?.id === tag.id &&
                                "tw-bg-[#6C26C4] tw-border-[#6C26C4] tw-shadow-md tw-font-bold",
                        )}
                        style={{
                            minWidth: "fit-content",
                            zIndex: 1,
                            display: "flex",
                            alignItems: "center",
                            gap: "0.25rem",
                        }}
                        onClick={onTagClick ? () => onTagClick(tag) : undefined}
                    >
                        {tag.label}
                        {canDeleteTag(tag) && (
                            <IoClose
                                className="tw-text-red-500 tw-cursor-pointer"
                                style={{ marginLeft: 4, fontSize: 15, position: "static" }}
                                onClick={(e) => handleDeleteClick(e, tag)}
                            />
                        )}
                    </button>
                ))}
                {/* Spacer to allow scrolling last tag fully into view */}
                {showExtra && hiddenCount > 0 && (
                    <div style={{ width: plusNWidth, minWidth: plusNWidth, flex: "none" }} aria-hidden />
                )}
                {showExtra && hiddenCount > 0 && (
                    <div
                        className="tw-sticky tw-right-0 tw-z-10"
                        style={{
                            right: 0,
                            background: "linear-gradient(to right, rgba(255,255,255,0.7) 60%, #fff 100%)",
                            marginLeft: "0.5rem",
                            display: "flex",
                            alignItems: "center",
                        }}
                    >
                        <button
                            className="tw-bg-[#f3f0ff] tw-text-[#6c47ff] tw-font-semibold tw-px-3 tw-py-1 tw-rounded-full tw-text-xs tw-whitespace-nowrap tw-border tw-border-[#e0d7ff] hover:tw-bg-[#e9e3ff] tw-transition-colors"
                            style={{ minWidth: "fit-content" }}
                            onClick={handlePlusNClick}
                        >
                            +{hiddenCount}
                        </button>
                    </div>
                )}
            </div>

            <DeleteTag
                tag={tagToDelete}
                propertyId={propertyId}
                open={showDeleteConfirm}
                onSuccess={handleDeleteSuccess}
                onCancel={handleDeleteCancel}
            />
        </>
    );
};

export default TagList;
